// src/components/admin/tabs/HealthTab.jsx
// 系统健康：正在进行的任务 + 最近报错，每 5 秒轮询 /api/admin/dashboard。
// 从 AdminDashboard.jsx 抽取，仅改造视觉为 admin 工作台语言，数据与轮询逻辑原样保留。
import { appConfirm } from '../../../services/appDialogs';
import React, { useCallback, useState, useRef } from "react";
import { RefreshCw, Clock, AlertTriangle, Power, ShieldAlert } from "lucide-react";
import { authenticatedFetch } from "../../../utils/api";
import { API_HOST } from "../../../utils/constants";
import { fetchGenerationMaintenance, updateGenerationMaintenance } from "../../../services/adminService";
import { useSequentialPolling } from "../../../hooks/useSequentialPolling";
import {
  Panel,
  EmptyBlock,
  StatusBadge,
  AdminScopeFilterBar,
  PrimaryButton,
  DangerButton,
  ErrorNotice,
} from "../adminUi.jsx";
import { displayUserId, useAdminScope, rowMatchesAdminScope } from "../adminUtils";

const ACCENT = "var(--color-warning)";
const DEFAULT_DAILY_COST_LIMIT_CNY = 20000;
const cnyFormatter = new Intl.NumberFormat("zh-CN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const shanghaiDateFormatter = new Intl.DateTimeFormat("zh-CN", {
  timeZone: "Asia/Shanghai",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const shanghaiDateTimeFormatter = new Intl.DateTimeFormat("zh-CN", {
  timeZone: "Asia/Shanghai",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const finiteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const formatCny = (value) => `¥${cnyFormatter.format(Math.max(0, finiteNumber(value)))}`;

const formatShanghaiDateTime = (value) => {
  if (!value) return "次日 00:00";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : shanghaiDateTimeFormatter.format(date);
};

const HealthTab = ({ projectId, projectOptions }) => {
  const [data, setData] = useState({ activeTasks: [], errorLogs: [] });
  const [loading, setLoading] = useState(false);
  // 仅用于首屏骨架屏展示，不影响数据逻辑
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [scope, setScope] = useAdminScope(projectId);
  const [generationControl, setGenerationControl] = useState(null);
  const [controlError, setControlError] = useState("");
  const [controlSaving, setControlSaving] = useState(false);
  const controlSavingRef = useRef(false);
  const controlRevisionRef = useRef(0);

  const fetchData = useCallback(async ({ signal } = {}) => {
    setLoading(true);
    const controlRevision = controlRevisionRef.current;
    const [dashboardResult, controlResult] = await Promise.allSettled([
      authenticatedFetch(API_HOST + "/api/admin/dashboard", { signal, timeout: 45_000 }),
      fetchGenerationMaintenance({ signal }),
    ]);
    if (signal?.aborted) return;
    if (dashboardResult.status === "fulfilled") {
      setData(dashboardResult.value);
      setLoadedOnce(true);
    } else {
      console.error(dashboardResult.reason);
    }
    if (
      controlResult.status === "fulfilled"
      && !controlSavingRef.current
      && controlRevision === controlRevisionRef.current
    ) {
      setGenerationControl(controlResult.value);
      setControlError("");
    } else if (controlResult.status === "rejected" && !controlSavingRef.current) {
      console.error(controlResult.reason);
      setControlError(controlResult.reason?.message || "生成开关状态加载失败");
    }
    setLoading(false);
  }, []);

  const refreshData = useSequentialPolling(fetchData, 5000);

  const activeTasks = (data.activeTasks || []).filter(row => rowMatchesAdminScope(row, scope));
  const errorLogs = (data.errorLogs || []).filter(row => rowMatchesAdminScope(row, scope));
  const firstLoading = loading && !loadedOnce;
  // 兼容旧接口：manualBlocked 尚未返回时，原 blocked 字段视作人工开关状态。
  const manualBlocked = generationControl?.manualBlocked ?? generationControl?.blocked ?? false;
  const dailyCostBlocked = generationControl?.dailyCostBlocked ?? false;
  const effectiveBlocked = generationControl?.blocked ?? (manualBlocked || dailyCostBlocked);
  const todayCostCny = Math.max(0, finiteNumber(generationControl?.todayCostCny));
  const configuredLimit = finiteNumber(generationControl?.dailyLimitCny, DEFAULT_DAILY_COST_LIMIT_CNY);
  const dailyLimitCny = configuredLimit > 0 ? configuredLimit : DEFAULT_DAILY_COST_LIMIT_CNY;
  const calculatedRemaining = Math.max(0, dailyLimitCny - todayCostCny);
  const remainingCny = Math.max(0, finiteNumber(generationControl?.remainingCny, calculatedRemaining));
  const budgetPercent = Math.min(100, Math.max(0, (todayCostCny / dailyLimitCny) * 100));
  const businessDate = generationControl?.businessDate || shanghaiDateFormatter.format(new Date());
  const autoResumeAt = generationControl?.autoResumeAt || generationControl?.nextResetAt;
  const unpricedBillableTaskCount = Math.max(0, Math.trunc(finiteNumber(generationControl?.unpricedBillableTaskCount)));

  const toggleGenerationMaintenance = async () => {
    if (!generationControl || controlSaving) return;
    const currentManualBlocked = generationControl.manualBlocked ?? generationControl.blocked ?? false;
    const nextBlocked = !currentManualBlocked;
    const confirmed = (await appConfirm(nextBlocked
      ? "确认人工暂停其他账号的新生成请求？已登录的李育东仍可提交；已提交任务继续处理和回传结果。"
      : dailyCostBlocked
        ? "确认解除人工暂停？今日人民币消耗已达到上限，解除后费用熔断仍限制所有账号（包括李育东）提交。"
        : "确认解除人工暂停并恢复其他账号的新生成请求？"));
    if (!confirmed) return;

    setControlSaving(true);
    controlSavingRef.current = true;
    controlRevisionRef.current += 1;
    setControlError("");
    try {
      const updated = await updateGenerationMaintenance(nextBlocked);
      controlRevisionRef.current += 1;
      setGenerationControl(updated);
    } catch (error) {
      controlRevisionRef.current += 1;
      setControlError(error?.message || "生成开关更新失败");
    } finally {
      controlSavingRef.current = false;
      setControlSaving(false);
    }
  };

  const formatUpdatedAt = (value) => {
    if (!value) return "尚未修改";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("zh-CN", { hour12: false });
  };

  return (
    <div>
      <Panel
        className="mb-4"
        title={(
          <span className="inline-flex items-center gap-1.5">
            <Power size={14} style={{ color: !generationControl ? "var(--color-text-muted)" : effectiveBlocked ? "var(--color-error)" : "var(--color-success)" }} />
            生成服务总开关
          </span>
        )}
        right={generationControl && (
          <StatusBadge
            label={effectiveBlocked ? "生成受限" : "生成正常"}
            color={effectiveBlocked ? "var(--color-error)" : "var(--color-success)"}
          />
        )}
      >
        {controlError && <ErrorNotice message={controlError} />}
        <div
          className="rounded-lg p-4 flex flex-col md:flex-row md:items-center justify-between gap-4"
          style={{
            backgroundColor: effectiveBlocked ? "var(--color-red-light)" : "var(--color-bg-overlay)",
            border: `1px solid ${effectiveBlocked ? "var(--color-error)" : "var(--color-border-secondary)"}`,
          }}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
              <ShieldAlert size={15} style={{ color: effectiveBlocked ? "var(--color-error)" : "var(--color-text-muted)" }} />
              {!generationControl
                ? "正在读取生成开关状态..."
                : manualBlocked && dailyCostBlocked
                  ? "人工暂停与每日费用熔断同时生效；所有账号均受费用熔断限制"
                  : manualBlocked
                    ? "其他账号的新生成与重试已暂停；已登录的李育东可继续提交"
                    : dailyCostBlocked
                      ? "今日费用达到上限，所有账号的新生成与重试均已暂停"
                      : "当前允许所有账号提交生成请求"}
            </div>
            <p className="text-xs mt-2 leading-5" style={{ color: "var(--color-text-secondary)" }}>
              限制只影响新的生成与重试请求。项目查看、历史记录、任务状态、下载、取消，以及已提交任务的后台轮询和结果保存都会继续运行。
            </p>
            {effectiveBlocked && (
              <p className="text-xs mt-1.5 font-medium" style={{ color: "var(--color-error)" }}>
                {dailyCostBlocked ? "用户提示" : "其他账号提示"}：{generationControl.message}
              </p>
            )}
            {dailyCostBlocked && (
              <p className="text-xs mt-1.5 font-medium" style={{ color: "var(--color-error)" }}>
                每日费用熔断限制所有账号（包括李育东），持续到北京时间 {formatShanghaiDateTime(autoResumeAt)}；届时今日消耗从 0 重新计算。
                {manualBlocked ? " 人工暂停不会随自然日自动解除。" : ""}
              </p>
            )}
            <div className="text-2xs mt-2" style={{ color: "var(--color-text-muted)" }}>
              {generationControl?.updatedBy ? `最近修改：${generationControl.updatedBy} · ` : ""}
              {formatUpdatedAt(generationControl?.updatedAt)}
            </div>
          </div>
          <div className="shrink-0">
            {!generationControl ? (
              <PrimaryButton disabled>读取中...</PrimaryButton>
            ) : manualBlocked ? (
              <PrimaryButton onClick={toggleGenerationMaintenance} disabled={controlSaving || !generationControl}>
                {controlSaving ? "处理中..." : "解除人工暂停"}
              </PrimaryButton>
            ) : dailyCostBlocked ? (
              <span
                className="inline-flex h-8 items-center rounded-md border px-3.5 text-xs font-medium"
                style={{
                  borderColor: "rgba(239,68,68,0.3)",
                  color: "var(--color-error)",
                  backgroundColor: "var(--color-red-light)",
                }}
              >
                次日自动恢复
              </span>
            ) : (
              <DangerButton onClick={toggleGenerationMaintenance} disabled={controlSaving || !generationControl}>
                {controlSaving ? "处理中..." : "暂停其他账号生成"}
              </DangerButton>
            )}
          </div>
        </div>
        {generationControl && (
          <div className="mt-3 rounded-lg p-4" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-secondary)" }}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <div className="text-label">今日人民币消耗</div>
                <div className="font-mono-num text-lg font-semibold mt-1" style={{ color: dailyCostBlocked ? "var(--color-error)" : ACCENT }}>
                  {formatCny(todayCostCny)}
                </div>
              </div>
              <div>
                <div className="text-label">每日熔断上限</div>
                <div className="font-mono-num text-lg font-semibold mt-1" style={{ color: "var(--color-text-primary)" }}>
                  {formatCny(dailyLimitCny)}
                </div>
              </div>
              <div>
                <div className="text-label">今日剩余</div>
                <div className="font-mono-num text-lg font-semibold mt-1" style={{ color: remainingCny > 0 ? "var(--color-success)" : "var(--color-error)" }}>
                  {formatCny(remainingCny)}
                </div>
              </div>
            </div>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
              <div
                className="h-full rounded-full transition-[width] duration-300"
                style={{
                  width: `${budgetPercent}%`,
                  backgroundColor: dailyCostBlocked ? "var(--color-error)" : ACCENT,
                }}
              />
            </div>
            <div className="mt-2 flex flex-col gap-1 text-2xs sm:flex-row sm:items-center sm:justify-between" style={{ color: "var(--color-text-muted)" }}>
              <span>北京时间自然日：{businessDate} 00:00 – 次日 00:00</span>
              <span>达到 {formatCny(dailyLimitCny)} 后暂停所有账号新生成，次日从 0 重新检测</span>
            </div>
            {unpricedBillableTaskCount > 0 && (
              <div
                className="mt-3 flex items-start gap-2 rounded-md px-3 py-2 text-xs"
                style={{ color: "var(--color-warning)", backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)" }}
              >
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                今日有 {unpricedBillableTaskCount} 个应计费终态任务尚无有效人民币价格，当前金额可能偏低，请检查对应厂商模型定价。
              </div>
            )}
          </div>
        )}
      </Panel>
      <AdminScopeFilterBar
        scope={scope}
        setScope={setScope}
        projectOptions={projectOptions}
        onRefresh={refreshData}
        loading={loading}
      />
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>每 <span className="font-mono-num">5</span> 秒自动刷新</div>
        <button
          type="button"
          onClick={refreshData}
          title="刷新"
          aria-label="刷新"
          className="w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer transition-colors duration-150"
          style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-secondary)" }}
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel
          title={(
            <span className="inline-flex items-center gap-1.5">
              <Clock size={13} style={{ color: 'var(--color-info)' }} />
              正在进行的任务
              <span className="font-mono-num" style={{ color: 'var(--color-text-muted)' }}>({activeTasks.length})</span>
            </span>
          )}
        >
          {firstLoading ? (
            <div className="space-y-2">
              <div className="skeleton" style={{ height: 56 }} />
              <div className="skeleton" style={{ height: 56 }} />
              <div className="skeleton" style={{ height: 56 }} />
            </div>
          ) : !activeTasks.length ? (
            <EmptyBlock text="当前系统空闲" hint="没有正在进行的任务" />
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto custom-scrollbar -mr-1 pr-1">
              {activeTasks.map((task, i) => (
                <div key={i} className="rounded-md p-2.5" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-secondary)' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="font-mono-num text-2xs px-1.5 py-0.5 rounded"
                      style={{ color: ACCENT, backgroundColor: 'color-mix(in srgb, var(--color-warning) 12%, transparent)' }}
                    >
                      {displayUserId(task.userId, 'Unknown')}
                    </span>
                    <StatusBadge label={task.taskType || '-'} color="var(--color-info)" />
                  </div>
                  <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{task.details}</div>
                  <div className="text-xs mt-1" style={{ color: 'var(--color-success)' }}>{task.currentStep}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel
          title={(
            <span className="inline-flex items-center gap-1.5">
              <AlertTriangle size={13} style={{ color: 'var(--color-error)' }} />
              最近报错
              <span className="font-mono-num" style={{ color: 'var(--color-text-muted)' }}>({errorLogs.length})</span>
            </span>
          )}
        >
          {firstLoading ? (
            <div className="space-y-2">
              <div className="skeleton" style={{ height: 56 }} />
              <div className="skeleton" style={{ height: 56 }} />
              <div className="skeleton" style={{ height: 56 }} />
            </div>
          ) : !errorLogs.length ? (
            <EmptyBlock text="系统运行正常" hint="最近没有报错记录" />
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto custom-scrollbar -mr-1 pr-1">
              {errorLogs.map((log, i) => (
                <div key={i} className="rounded-md p-2.5" style={{ backgroundColor: 'var(--color-red-light)', border: '1px solid var(--color-error)' }}>
                  <div className="flex justify-between items-baseline text-xs mb-1">
                    <span className="font-semibold" style={{ color: 'var(--color-error)' }}>{log.context}</span>
                    <span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{log.formattedTime}</span>
                  </div>
                  <div className="text-xs break-all font-mono-num" style={{ color: 'var(--color-text-secondary)' }}>{log.errorMessage}</div>
                  <div className="text-2xs mt-1" style={{ color: 'var(--color-text-muted)' }}>User: {displayUserId(log.userId, 'Unknown')}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default HealthTab;
